import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Feather, CheckCircle, Star } from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import AuthorFilters from '../components/AuthorFilters';
import { proxyImage } from '../lib/imageProxy';

const API = process.env.REACT_APP_BACKEND_URL;

const CommunityWriters = () => {
  const [authors, setAuthors] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    search: '',
    alphabet: '',
    is_verified: null,
    is_featured: null,
    state: '',
    country: '',
    sort_by: 'followers_count'
  });

  useEffect(() => {
    // Fetch filter options for community writers
    axios.get(`${API}/api/community-writers/filters`)
      .then(r => setFilterOptions(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    
    // Build query string
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        params.append(key, value);
      }
    });

    // Use the community-writers endpoint
    axios.get(`${API}/api/community-writers?${params.toString()}`)
      .then(r => {
        setAuthors(r.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);  // No need to force is_legendary anymore
  };

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-3">
            Community Writers
          </h1>
          <p className="text-lg text-muted-foreground">
            Explore contemporary authors and emerging voices on Kavyalekh — today's storytellers and poets.
          </p>
        </div>

        {/* Filters */}
        <AuthorFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
          showCommunityFilters={true}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* Results */}
        <div className="mt-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
            </div>
          ) : authors.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No writers found matching your filters.</p>
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-muted-foreground">
                Found {authors.length} writer{authors.length !== 1 ? 's' : ''}
              </div>

              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {authors.map((author) => (
                    <Link
                      key={author.author_id}
                      to={`/writer/${author.author_id}`}
                      className="group"
                    >
                      <div className="relative overflow-hidden rounded-full aspect-square mb-4 border-4 border-border bg-muted hover:-translate-y-1 transition-transform">
                        {author.photograph ? (
                          <img 
                            src={proxyImage(author.photograph)} 
                            alt={author.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                            <Feather size={48} className="text-primary/40" weight="duotone" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          {author.is_verified && (
                            <div className="bg-blue-500 text-white rounded-full p-1" title="Verified">
                              <CheckCircle size={16} weight="fill" />
                            </div>
                          )}
                          {author.is_featured && (
                            <div className="bg-amber-500 text-white rounded-full p-1" title="Featured">
                              <Star size={16} weight="fill" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <p className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                            {author.name}
                          </p>
                        </div>
                        {author.state && (
                          <p className="text-xs text-muted-foreground mt-1">{author.state}</p>
                        )}
                        <div className="flex items-center justify-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{author.works_count || 0} works</span>
                          <span>•</span>
                          <span>{author.followers_count || 0} followers</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {authors.map((author) => (
                    <Link
                      key={author.author_id}
                      to={`/writer/${author.author_id}`}
                      className="flex items-center gap-4 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors bg-background"
                    >
                      <div className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-border bg-muted flex-shrink-0">
                        {author.photograph ? (
                          <img 
                            src={proxyImage(author.photograph)} 
                            alt={author.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                            <Feather size={24} className="text-primary/40" weight="duotone" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-serif text-xl font-semibold text-foreground">
                            {author.name}
                          </h3>
                          {author.is_verified && (
                            <CheckCircle size={16} weight="fill" className="text-blue-500" title="Verified" />
                          )}
                          {author.is_featured && (
                            <Star size={16} weight="fill" className="text-amber-500" title="Featured" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {author.state && `${author.state}, `}{author.country || 'India'}
                        </p>
                        {author.biography && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {author.biography}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div>{author.works_count || 0} works</div>
                        <div>{author.followers_count || 0} followers</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CommunityWriters;
